import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Ansi from 'ansi-to-react';

import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';

import styles from './esp32-upload-progress.css';

const Esp32UploadProgressComponent = props => (
    <Modal
        className={styles.modalContent}
        contentLabel="ESP32-S3 Upload"
        headerClassName={styles.header}
        id="esp32UploadProgressModal"
        onRequestClose={props.onClose}
        shouldCloseOnOverlayClick={false}
        closeButtonVisible={false}
    >
        <Box className={styles.body}>
            <Box className={styles.title}>
                {props.phase === 'uploading' ? (
                    <FormattedMessage
                        defaultMessage="Uploading to ESP32-S3"
                        description="ESP32 upload progress title"
                        id="gui.esp32UploadProgress.title"
                    />
                ) : props.phase === 'success' ? (
                    <FormattedMessage
                        defaultMessage="Upload Success"
                        description="ESP32 upload success title"
                        id="gui.esp32UploadProgress.successTitle"
                    />
                ) : (
                    <FormattedMessage
                        defaultMessage="Upload Error"
                        description="ESP32 upload error title"
                        id="gui.esp32UploadProgress.errorTitle"
                    />
                )}
            </Box>

            {/* Initialize Progress (Download + Extract) */}
            {props.initProgress && props.initProgress.percent > 0 && props.initProgress.percent < 100 ? (
                <Box className={styles.progressSection}>
                    <Box className={styles.progressInfo}>
                        <FormattedMessage
                            defaultMessage="Initializing"
                            description="Initialize progress label"
                            id="gui.esp32UploadProgress.initializing"
                        />
                        : {props.initProgress.percent}%
                    </Box>
                    <Box className={styles.progressBar}>
                        <Box 
                            className={styles.progressFill}
                            style={{width: `${props.initProgress.percent}%`}}
                        />
                    </Box>
                </Box>
            ) : null}

            {/* Flash Progress (Flashing to device) */}
            {props.flashProgress && props.flashProgress.percent > 0 ? (
                <Box className={styles.progressSection}>
                    <Box className={styles.progressInfo}>
                        <FormattedMessage
                            defaultMessage="Flash Progress"
                            description="Flash progress label"
                            id="gui.esp32UploadProgress.flashProgress"
                        />
                        : {props.flashProgress.percent}%
                    </Box>
                    <Box className={styles.progressBar}>
                        <Box 
                            className={classNames(
                                styles.progressFill,
                                props.phase === 'error' && styles.progressFillError,
                                props.phase === 'success' && styles.progressFillSuccess
                            )}
                            style={{width: `${props.flashProgress.percent}%`}}
                        />
                    </Box>
                </Box>
            ) : null}

            {/* Logs/Text output */}
            {props.text ? (
                <Box className={styles.logsContainer}>
                    <Box className={styles.logsTitle}>
                        <FormattedMessage
                            defaultMessage="Details"
                            description="Details section title"
                            id="gui.esp32UploadProgress.details"
                        />
                    </Box>
                    <Box className={styles.logs}>
                        <Ansi>
                            {props.text}
                        </Ansi>
                    </Box>
                </Box>
            ) : null}

            <Box className={styles.buttonContainer}>
                {props.phase === 'uploading' ? (
                    <button
                        className={classNames(
                            styles.button, 
                            styles.abortButton,
                            !props.abortEnabled && styles.buttonDisabled
                        )}
                        onClick={props.onAbort}
                        disabled={!props.abortEnabled}
                    >
                        <FormattedMessage
                            defaultMessage="Abort"
                            description="Abort button"
                            id="gui.esp32UploadProgress.abort"
                        />
                    </button>
                ) : (
                    <button
                        className={classNames(styles.button, styles.closeButton)}
                        onClick={props.onClose}
                    >
                        <FormattedMessage
                            defaultMessage="Close"
                            description="Close button"
                            id="gui.esp32UploadProgress.close"
                        />
                    </button>
                )}
            </Box>
        </Box>
    </Modal>
);

Esp32UploadProgressComponent.propTypes = {
    text: PropTypes.string,
    phase: PropTypes.oneOf(['uploading', 'success', 'error']),
    abortEnabled: PropTypes.bool,
    initProgress: PropTypes.shape({
        percent: PropTypes.number
    }),
    flashProgress: PropTypes.shape({
        fileIndex: PropTypes.number,
        percent: PropTypes.number
    }),
    onAbort: PropTypes.func,
    onClose: PropTypes.func.isRequired
};

Esp32UploadProgressComponent.defaultProps = {
    text: '',
    phase: 'uploading',
    abortEnabled: false,
    initProgress: null,
    flashProgress: null,
    onAbort: () => {}
};

export default Esp32UploadProgressComponent;
